import React from "react";
import { Tooltip, IconButton, Paper, Typography, CircularProgress } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@material-ui/icons/Close";
import { useMaterialUIController } from "context";
import { application } from "globals/endpoints";

// Shared ref to track the currently active player's close function
const activePlayer = { current: null };

export default function FsVoicePickerPlayAudio({ row }) {
  const encodedPath = row.original.id;
  const caller = row.original.caller || "";
  const destination = row.original.destinationNumber || "";
  const direction = row.original.direction || "";
  const dateTime = row.original.dateTime
    ? new Date(row.original.dateTime.replace(" ", "T")).toLocaleString()
    : "";
  const title = `${caller} → ${destination}  (${direction})  ${dateTime}`;
  const [open, setOpen] = React.useState(false);
  const [audioUrl, setAudioUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const cleanup = React.useCallback(() => {
    setOpen(false);
    if (audioUrl) {
      window.URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  }, [audioUrl]);

  const handleClickOpen = () => {
    // Close any other active player first
    if (activePlayer.current && activePlayer.current !== cleanup) {
      activePlayer.current();
    }

    if (audioUrl) {
      setOpen(true);
      activePlayer.current = cleanup;
      return;
    }

    setLoading(true);
    application
      .get(`/fsVoicePicker/play?path=${encodeURIComponent(encodedPath)}`, {
        responseType: "blob",
      })
      .then((response) => {
        const blobUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: "audio/mpeg" })
        );
        setAudioUrl(blobUrl);
        setOpen(true);
        activePlayer.current = () => {
          setOpen(false);
          window.URL.revokeObjectURL(blobUrl);
          setAudioUrl(null);
        };
      })
      .catch((error) => {
        console.error("Error loading audio:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    cleanup();
    if (activePlayer.current === cleanup) {
      activePlayer.current = null;
    }
  };

  React.useEffect(() => {
    return () => {
      if (audioUrl) {
        window.URL.revokeObjectURL(audioUrl);
      }
      if (activePlayer.current === cleanup) {
        activePlayer.current = null;
      }
    };
  }, [audioUrl, cleanup]);

  return (
    <>
      <Tooltip title="Play">
        <IconButton
          color="primary"
          onClick={handleClickOpen}
          disabled={loading}
          size="small"
        >
          {loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
        </IconButton>
      </Tooltip>
      {open && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
            padding: "12px 16px",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            backgroundColor: darkMode ? "#1a2035" : "#ffffff",
            minWidth: "450px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              variant="caption"
              fontWeight="medium"
              sx={{
                color: darkMode ? "#ffffffcc" : "#344767",
                maxWidth: "390px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            <IconButton size="small" onClick={handleClose} sx={{ color: darkMode ? "#ffffffcc" : "#344767" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          {audioUrl && (
            <audio
              src={audioUrl}
              autoPlay
              controls
              style={{
                width: "100%",
                height: "40px",
                outline: "none",
              }}
            />
          )}
        </Paper>
      )}
    </>
  );
}
